import React, { useEffect } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  Tooltip,
  LabelList,
} from "recharts";
import "../../assets/styles/common/rechert.scss";
import { GroupData } from "../../types/GroupData";

interface Props {
  groups: GroupData[];
}

const ScoreBarChart: React.FC<Props> = ({ groups }) => {
  useEffect(() => {}, [groups]);

  const chartData = groups.map((group) => {
    const mus1 = Number(group.songResultMus1 ?? 0);
    const per1 = Number(group.songResultPer1 ?? 0);
    const sng1 = Number(group.songResultSng1 ?? 0);
    const score1 = mus1 + per1 + sng1;
    const song1 = group.songName1;

    const mus2 = Number(group.songResultMus2 ?? 0);
    const per2 = Number(group.songResultPer2 ?? 0);
    const sng2 = Number(group.songResultSng2 ?? 0);
    const score2 = mus2 + per2 + sng2;
    const song2 = group.songName2;

    const mus3 = Number(group.songResultMus3 ?? 0);
    const per3 = Number(group.songResultPer3 ?? 0);
    const sng3 = Number(group.songResultSng3 ?? 0);
    const score3 = mus3 + per3 + sng3;
    const song3 = group.songName3;

    const mus4 = Number(group.songResultMus4 ?? 0);
    const per4 = Number(group.songResultPer4 ?? 0);
    const sng4 = Number(group.songResultSng4 ?? 0);
    const score4 = mus4 + per4 + sng4;
    const song4 = group.songName4;

    const hasFinal = score3 > 0 && score4 > 0;
    const scoreAll = score1 + score2 + score3 + score4;

    return {
      year: group.createdAt?.toDate().getFullYear() + " " + group.entryClass,
      labelMus1: `${group.songPenaltyMus1 ? "-" : ""}${mus1}`,
      labelPer1: `${group.songPenaltyPer1 ? "-" : ""}${per1}`,
      labelSng1: `${group.songPenaltySng1 ? "-" : ""}${sng1}`,
      score1,
      song1,
      labelMus2: `${group.songPenaltyMus2 ? "-" : ""}${mus2}`,
      labelPer2: `${group.songPenaltyPer2 ? "-" : ""}${per2}`,
      labelSng2: `${group.songPenaltySng2 ? "-" : ""}${sng2}`,
      score2,
      song2,
      ...(hasFinal && {
        labelMus3: `${group.songPenaltyMus3 ? "-" : ""}${mus3}`,
        labelPer3: `${group.songPenaltyPer3 ? "-" : ""}${per3}`,
        labelSng3: `${group.songPenaltySng3 ? "-" : ""}${sng3}`,
        score3,
        song3,
        labelMus4: `${group.songPenaltyMus4 ? "-" : ""}${mus4}`,
        labelPer4: `${group.songPenaltyPer4 ? "-" : ""}${per4}`,
        labelSng4: `${group.songPenaltySng4 ? "-" : ""}${sng4}`,
        score4,
        song4,
      }),
      scoreAll,
    };
  });
  return (
    <div style={{ width: "95%", height: 400 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />

          <XAxis dataKey="year" height={60} />
          <YAxis domain={[0, 300]} />
          <Tooltip />

          {[1, 2, 3, 4].map((num) => {
            if (
              (num === 3 || num === 4) &&
              !chartData.some((item) => item.score3)
            ) {
              return null;
            }

            const color =
              num <= 2
                ? ["#4f46e5", "#6366f1", "#818cf8"]
                : ["#059669", "#10b981", "#6ee7b7"];

            return (
              <React.Fragment key={num}>
                <Bar
                  dataKey={`score${num}`}
                  stackId={`score${num}`}
                  fill={color[0]}
                  barSize={34}
                  radius={[16, 16, 0, 0]}
                >
                  {/* 曲名 */}
                  <LabelList
                    content={({ x, y, width, height, index }) => {
                      if (
                        x == null ||
                        y == null ||
                        width == null ||
                        height == null ||
                        index == null
                      ) {
                        return null;
                      }

                      const song = String(
                        chartData[index]?.[
                          `song${num}` as keyof (typeof chartData)[number]
                        ] ?? "",
                      );

                      // 高さに応じて表示文字数を決定
                      const maxChars = Math.max(
                        2,
                        Math.floor(Number(height) / 9),
                      );

                      const label =
                        song.length > maxChars
                          ? `${song.slice(0, maxChars - 3)}...`
                          : song;

                      const centerX = Number(x) + Number(width) / 2;
                      const textY = Number(y) + Number(height) - 12;

                      return (
                        <text
                          x={centerX}
                          y={textY}
                          fill="#fff"
                          fontSize={10}
                          textAnchor="left"
                          dominantBaseline="middle"
                          transform={`rotate(-90 ${centerX} ${textY})`}
                          pointerEvents="none"
                          fontFamily="Consolas, Monaco, monospace"
                          letterSpacing={1}
                          fontWeight={400}
                        >
                          {label}
                        </text>
                      );
                    }}
                  />

                  <LabelList
                    dataKey={`score${num}`}
                    position="insideTop"
                    offset={14}
                    fontSize={12}
                    fontWeight="bold"
                    fill="#fff"
                  />
                </Bar>
              </React.Fragment>
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ScoreBarChart;
